import React, { Suspense, useEffect, useState } from 'react';
// import PropTypes from 'prop-types';

import { useSelector } from 'react-redux';
import { createBrowserHistory } from 'history';
import { Route, Routes } from 'react-router-dom';

import GoogleTranslate from '../components/GoogleTranslate';
import LandingPageBanner from '../components/LandingPageBanner';
import NavBar from '../components/NavBar';
import Resources from '../components/static/from-config/Resources';

import {
  AppWrapper,
  CustomRouter,
  lazyWithRetry,
  PrivateRoute,
  PublicRoute,
  ScrollToTop,
} from './helpers';
import { Loading, NotFoundPage, Footer } from '../../dronebee-ui';
// import { useLogoutMutation } from '../redux/reducers/auth';
import { useGetConfigLandingPageQuery } from '../redux/reducers/config';
import { useLazyDownloadTemplateDocumentQuery } from '../redux/reducers/documents';

const config = programConfig;

// AUTH
const LogIn = lazyWithRetry(() => import('../components/auth/LogIn'));
const Register = lazyWithRetry(() => import('../components/auth/Register'));
const ValidateMFA = lazyWithRetry(() => import('../components/auth/ValidateMFA'));
const ForgotPassword = lazyWithRetry(() => import('../components/auth/ForgotPassword'));
const ResetPassword = lazyWithRetry(() => import('../components/auth/ResetPassword'));
const ResendInvitation = lazyWithRetry(() => import('../components/auth/ResendInvitation'));

// APPLICATION
const Dashboard = lazyWithRetry(() => import('../components/applications/Dashboard'));
const HomeDetails = lazyWithRetry(() => import('../components/applications/pages/HomeDetailsPage'));
const MortgageLien = lazyWithRetry(() => import('../components/applications/pages/MortgageLienPage'));
const Insurance = lazyWithRetry(() => import('../components/applications/pages/InsurancePage'));
const Homeowners = lazyWithRetry(() => import('../components/applications/pages/HomeownersPage'));

// STATIC
const LandingPage = lazyWithRetry(() => import('../components/static/LandingPage'));
const FAQ = lazyWithRetry(() => import('../components/static/FAQ'));
const RequiredDocuments = lazyWithRetry(() => import('../components/static/RequiredDocuments'));
const MessageCenter = lazyWithRetry(() => import('../components/static/MessageCenter'));

// TEST
const TestPage = lazyWithRetry(() => import('../components/TestPage'));

export const history = createBrowserHistory();

const AppRouter = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  // const [logout] = useLogoutMutation();

  const  { data: landing_page = {} } = useGetConfigLandingPageQuery(undefined, { skip: isLoggedIn });
  const [getTemplateDocuments] = useLazyDownloadTemplateDocumentQuery();

  const [isTranslatorOpened, setTranslatorOpen] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      const observer = new MutationObserver((m) => {
        const [mutation] = m;
        if (mutation && mutation.target.firstElementChild.className === 'skiptranslate') {
          mutation.target.firstElementChild.style.display === 'none'
            ? setTranslatorOpen(false)
            : setTranslatorOpen(true);
        }
      });

      const [target] = document.getElementsByClassName('sidebar-fixed');
      observer.observe(target, { attributes: true, attributeFilter: ['style'] });
    }, 2000);
  }, []);

  return (
    <CustomRouter history={history}>
      <ScrollToTop>
        <div className='container-scroller'>
          {!isLoggedIn && landing_page?.is_banner_visible &&
            <>
              {/* FIXED LANDING PAGE BANNER */}
              <LandingPageBanner message={landing_page.message} />

              {/* INVISIBLE LANDING PAGE BANNER FOR APP WRAPPER TOP SPACING */}
              <div className='landing-page-banner invisible' />
            </>
          }

          <NavBar
            isTranslatorOpened={isTranslatorOpened}
            isLoggedIn={isLoggedIn}
            isBannerVisible={!isLoggedIn && landing_page?.is_banner_visible}
          />

          <AppWrapper>
            <Suspense fallback={<Loading main={true} />}>
              <Routes>
                {/* LANDING */}
                <Route path='/' element={<PublicRoute><LandingPage /></PublicRoute>} />

                {/* AUTH */}
                <Route path='/login' element={<PublicRoute><LogIn /></PublicRoute>} />
                <Route path='/register/:token_uuid' element={<PublicRoute><Register /></PublicRoute>} />
                <Route path='/validate/session' element={<PublicRoute><ValidateMFA /></PublicRoute>} />
                <Route path='/forgot-password' element={<PublicRoute><ForgotPassword /></PublicRoute>} />
                <Route path='/reset-password/:token_uuid' element={<PublicRoute><ResetPassword /></PublicRoute>} />
                <Route path='/resend-invitation' element={<PublicRoute><ResendInvitation /></PublicRoute>} />

                {/* SIGN_UP */}
                <Route path='/register' element={<PublicRoute><Register /></PublicRoute>} />

                {/* REGISTER MANUAL ACCOUNT */}
                <Route path='/register/:token?' element={<PublicRoute><Register /></PublicRoute>} />

                {/* APPLICATIONS */}
                <Route path='/application/:application_uuid?' element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path='/application/:application_uuid/home-details/edit' element={<PrivateRoute><HomeDetails history={history}  /></PrivateRoute>} />
                <Route path='/application/:application_uuid/mortgage-lien/edit' element={<PrivateRoute><MortgageLien history={history}  /></PrivateRoute>} />
                <Route path='/application/:application_uuid/insurance/edit' element={<PrivateRoute><Insurance history={history}  /></PrivateRoute>} />
                <Route path='/application/:application_uuid/home-owners/edit/:homeowner_uuid?' element={<PrivateRoute><Homeowners history={history} /></PrivateRoute>} />

                {/* STATIC */}
                {config.faqPage?.isVisible && <Route path='/faq/:application_uuid?' element={<FAQ />} />}
                {config.requiredDocumentsPage?.isVisible && <Route path='/required-documents/:application_uuid?' element={<RequiredDocuments />} />}

                {/* MESSAGE CENTER */}
                <Route path='/message-center/:application_uuid' element={<PrivateRoute><MessageCenter /></PrivateRoute>} />

                {/* CONFIG STATIC */}
                {config.staticPages.resources && <Route path='/resources' element={<PublicRoute><Resources /></PublicRoute>} />}

                {/* TEST */}
                {environment !== 'production' && <Route path='/test' element={<PublicRoute><TestPage /></PublicRoute>} />}

                <Route path='*' element={<NotFoundPage onClick={() => history.push('/')} />} />
              </Routes>
            </Suspense>
          </AppWrapper>

          <Footer
            onTosClick={() => getTemplateDocuments('TOS')}
            onPpClick={() => getTemplateDocuments('PP')}
            config={{ footer: config.footer, faqPage: config.faqPage }}
            history={history}
            version={VERSION}
            environment={environment}
            googleTranslate={<GoogleTranslate />} />
        </div>
      </ScrollToTop>
    </CustomRouter>
  );
};

AppRouter.propTypes = {
};

export default AppRouter;
